
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';

const NordvestLocation = () => {
  const features = [
    {
      title: "Tilgængelig fra Nordvest",
      description: "Undervisningslokaler på Niels Finsens Alle 86, 2860 Søborg, med god forbindelse fra Nordvest via offentlig transport.",
      icon: "location"
    },
    {
      title: "Fleksible tider",
      description: "Morgen-, eftermiddags- og aftentimer på hverdage samt weekendundervisning efter aftale.",
      icon: "clock"
    },
    {
      title: "Alle niveauer",
      description: "Fra nybegyndere til øvede - undervisning der er skræddersyet til dit niveau og dine mål.",
      icon: "music"
    },
    {
      title: "Konservatorieuddannet",
      description: "Undervisning af høj kvalitet fra Oscar Gilbert, som er uddannet på Rytmisk Musikkonservatorium.",
      icon: "award"
    }
  ];

  const testimonials = [
    {
      name: "Michael Andersen",
      location: "Nordvest",
      text: "Jeg pendler fra Nordvest til Søborg for at modtage undervisning fra Oscar, og det er hver rejse værd. Hans metoder er effektive og hans entusiasme smittende.",
      stars: 5
    },
    {
      name: "Nanna Thomsen",
      location: "Nordvest",
      text: "Oscar har et særligt talent for at gøre selv komplekse musikstykker forståelige og sjove at lære. Jeg har udviklet mig mere på et halvt år end jeg gjorde på flere år tidligere.",
      stars: 5
    },
    {
      name: "Frederik Larsen",
      location: "Nordvest",
      text: "Som voksen begynder var jeg nervøs for at starte til klaver, men Oscar skabte et trygt læringsmiljø fra første time. Hans tålmodighed og opmuntring har været afgørende for min fremgang.",
      stars: 5
    }
  ];

  return (
    <Layout>
      <div id="top"></div>
      <Helmet>
        <title>Klaverundervisning i Nordvest | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Nordvest med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Nordvest"
        description="Professionel klaverundervisning med Oscar Gilbert for alle fra Nordvest. Få en personlig tilgang til at lære klaver i behagelige omgivelser på Niels Finsens Alle 86."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Nordvest</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Oscar Gilbert tilbyder kvalitets klaverundervisning for elever fra Nordvest på Niels Finsens Alle 86 i Søborg. Med gode transportforbindelser er det nemt at komme til undervisningen.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Undervisningen foregår på et akustisk Yamaha-flygel i et lyst og inspirerende lokale, der skaber de perfekte rammer for en optimal læringsoplevelse.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Med sin baggrund fra Rytmisk Musikkonservatorium og mange års undervisningserfaring tilbyder Oscar en struktureret og personlig tilgang til klaverundervisning, der er skræddersyet til dine individuelle behov og musikalske mål.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt#top">Book Gratis Prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/om#top">Læs Mere Om Oscar</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Nordvest" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Nordvest" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, med nem adgang fra Nordvest.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d71893.0078265522!2d12.5220869!3d55.7297216!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743771839328!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Nordvest:</strong></p>
                    <p className="text-gray-700">• God forbindelse fra Nordvest med bus 6A</p>
                    <p className="text-gray-700">• Nem rute via Frederikssundsvej/Frederiksborgvej</p>
                    <p className="text-gray-700">• Gratis parkering foran bygningen</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline">+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Nordvest" />
    </Layout>
  );
};

export default NordvestLocation;
